import React from 'react';
import { Wallet, DollarSign, Calculator, Globe, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров валютной ипотеки
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  supportedCurrencies,
  currencyRates
}) => {
  // Расчет текущей суммы кредита
  const loanAmount = parseFloat(formData.loanAmount) || 0;
  
  // Расчет эквивалента в рублях
  const loanAmountRUB = loanAmount * (currencyRates[formData.currency] || 0);
  
  // Расчет текущего срока в месяцах
  const loanTermMonths = Math.round((parseFloat(formData.loanTerm) || 0) * 12);
  
  // Обработчик изменения суммы кредита
  const handleLoanAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      loanAmount: newValue,
      equivalentInRubles: Math.round(parseFloat(newValue || 0) * (currencyRates[formData.currency] || 1)).toString()
    }));
  };
  
  // Обработчик изменения срока кредита
  const handleLoanTermChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      loanTerm: newValue
    }));
  };
  
  // Обработчик изменения процентной ставки
  const handleInterestRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      interestRate: newValue
    }));
  };
  
  // Обработчик изменения валюты
  const handleCurrencyChange = (currency) => {
    const selectedCurrency = supportedCurrencies.find(c => c.id === currency);
    
    setFormData(prev => {
      const newRate = selectedCurrency ? selectedCurrency.defaultRate.toString() : prev.interestRate;
      const newAmount = prev.loanAmount;
      return {
        ...prev,
        currency: currency,
        interestRate: newRate,
        equivalentInRubles: Math.round(parseFloat(newAmount || 0) * (currencyRates[currency] || 1)).toString()
      };
    });
  };
  
  // Предопределенные суммы кредита для быстрого выбора
  const predefinedAmounts = [
    { value: '50000', label: '50K' },
    { value: '100000', label: '100K' },
    { value: '200000', label: '200K' },
    { value: '500000', label: '500K' }
  ];
  
  // Предопределенные сроки кредита для быстрого выбора
  const predefinedTerms = [
    { value: '5', label: '5 лет' },
    { value: '10', label: '10 лет' },
    { value: '15', label: '15 лет' },
    { value: '20', label: '20 лет' }
  ];
  
  // Функция расчета
  const calculateLoan = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Валюта кредита */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Валюта кредита</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.currency}</span>
          </div>
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['USD', 'EUR', 'CHF', 'CNY'].map(currency => (
              <button
                key={currency}
                type="button"
                onClick={() => handleCurrencyChange(currency)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.currency === currency 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {currency}
              </button>
            ))}
          </div>
        </div>
        
        {/* Сумма кредита */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="loanAmountSlider" className="text-gray-600 dark:text-gray-400">Сумма кредита ({formData.currency})</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatNumber(loanAmount, 0)} {formData.currency}
            </span>
          </div>
          <input
            type="range"
            id="loanAmountSlider"
            min="10000"
            max="1000000"
            step="10000"
            value={loanAmount}
            onChange={handleLoanAmountChange}
            aria-label="Сумма кредита"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Эквивалент в рублях */}
          <div className="text-xs text-center mt-1 text-gray-500 dark:text-gray-400">
            ≈ {formatNumber(loanAmountRUB, 0)} ₽
          </div>
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  loanAmount: amount.value,
                  equivalentInRubles: Math.round(parseFloat(amount.value) * (currencyRates[formData.currency] || 1)).toString()
                }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.loanAmount === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок кредита */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="loanTermSlider" className="text-gray-600 dark:text-gray-400">Срок кредита</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.loanTerm} {pluralize(parseFloat(formData.loanTerm) || 0, 'год', 'года', 'лет')} ({loanTermMonths} мес.)
            </span>
          </div>
          <input
            type="range"
            id="loanTermSlider"
            min="1"
            max="30"
            step="1"
            value={formData.loanTerm}
            onChange={handleLoanTermChange}
            aria-label="Срок кредита (лет)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedTerms.map(term => (
              <button
                key={term.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, loanTerm: term.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.loanTerm === term.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {term.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Процентная ставка */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="interestRateSlider" className="text-gray-600 dark:text-gray-400">Процентная ставка</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.interestRate}%</span>
          </div>
          <input
            type="range"
            id="interestRateSlider"
            min="1"
            max="10"
            step="0.1"
            value={formData.interestRate}
            onChange={handleInterestRateChange}
            aria-label="Процентная ставка (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные процентные ставки */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['3.5', '4.5', '5.5', '6.5'].map(rate => (
              <button
                key={rate}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, interestRate: rate }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.interestRate === rate 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {rate}%
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор типа платежа */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип платежа</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, paymentType: 'annuity' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.paymentType === 'annuity' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Аннуитетный</div>
              <div className="text-xs opacity-75">Равные платежи</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, paymentType: 'differentiated' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.paymentType === 'differentiated' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Дифференц.</div>
              <div className="text-xs opacity-75">Уменьшаемые</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateLoan}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать ипотеку
        </Button>
        
        {/* Информация о курсе */}
        <div className="text-center text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center">
          <Globe className="w-3.5 h-3.5 mr-1" />
          Курс 1 {formData.currency} = {formatNumber(currencyRates[formData.currency] || 0, 2)} ₽
        </div>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;